import React, { useEffect, useState } from 'react';
/**
 * `ModelTrimInput` loads and renders available model-trim options for the
 * current `vehicleDetails`.
 *
 * Behaviour:
 * - Calls `VehicleApi.getModelTrims` using the vehicle's registration date and
 *   core vehicle identifiers.
 * - Maps lookup results into `SelectInput` options.
 * - Emits both the selected id (`value`) and display text back to the parent
 *   via `onSelectedValueChange`.
 */
import {
  SelectOpt,
  VehicleInfoLookup,
  VehicleModelTrimLookup,
  VehicleType
} from '../CommonTypes';
import { VehicleApi } from '../../../api/VehicleApi';
import SelectInput from '../SelectInput/SelectInput';
import { useTranslation } from 'react-i18next';

type ModelTrimInputProps = {
  vehicleDetails: VehicleType;
  value: string;
  error: string;
  onSelectedValueChange: (value: string, text: string) => void;
  token: string;
};

const ModelTrimInput = ({
  vehicleDetails,
  value,
  error,
  onSelectedValueChange,
  token
}: ModelTrimInputProps): JSX.Element => {
  const [modelTrimOptions, setModelTrimOptions] = useState<SelectOpt[]>([]);

  const { t } = useTranslation();

  const onChange = (updatedValue: string) => {
    const updatedText =
      modelTrimOptions.find((o) => o.value == updatedValue)?.text ?? '';
    onSelectedValueChange(updatedValue, updatedText);
  };

  const registrationDateSplit = vehicleDetails.registrationDate.split('/');
  const registrationDate = new Date(
    parseInt(registrationDateSplit[2]),
    parseInt(registrationDateSplit[1]) - 1,
    parseInt(registrationDateSplit[0])
  );

  const mapVehicleSection = (
    vehicleInformation: VehicleInfoLookup[]
  ): SelectOpt[] => {
    const mappedVehicleOptions: SelectOpt[] = [];
    vehicleInformation.forEach((info) => {
      const option = {
        id: info.id,
        text: info.text,
        value: info.value,
        disabled: false
      };
      mappedVehicleOptions.push(option);
    });
    return mappedVehicleOptions;
  };

  const successCallbackModelTrims = (data: VehicleModelTrimLookup[]) => {
    const mappedModelTrims = mapVehicleSection(data);
    setModelTrimOptions(mappedModelTrims);
  };

  const failureCallbackVehicleInfo = () => {
    console.log('Failed to get with: ', error);
  };

  useEffect(() => {
    const abortController = new AbortController();
    new VehicleApi().getModelTrims(
      registrationDate.toDateString(),
      vehicleDetails.vehicleTypeId,
      vehicleDetails.makeId,
      vehicleDetails.modelId,
      vehicleDetails.fuelTypeId.toString(),
      vehicleDetails.engineSize,
      token,
      successCallbackModelTrims,
      failureCallbackVehicleInfo,
      abortController.signal
    );
  }, [vehicleDetails]);

  return (
    <SelectInput
      name='model-trim-input'
      className='input-text large'
      value={value}
      options={modelTrimOptions}
      onChange={onChange}
      defaultText='Select...'
      forceDefault
      addDefaultOption
      error={error}
      loadingText={t('loading-placeholder.loading-text')}
    />
  );
};
export default ModelTrimInput;
